import React, {
	useEffect,
	useMemo,
	useRef,
	useState,
	useCallback,
} from "react";
import {
	GoogleMap,
	Marker,
	InfoWindow,
	MarkerClustererF,
	OverlayView,
} from "@react-google-maps/api";
import {
	HourglassFull,
	Moving,
	EvStation,
	ElectricRickshaw,
	WarningAmber,
	CloseRounded,
} from "@mui/icons-material";
import {
	Box,
	Typography,
	Grid,
	Badge,
	Button,
	Tooltip,
	Rating,
	Paper,
	IconButton,
} from "@mui/material";
import { fetchDealerScore } from "../../api/dealers";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const BACKEND_TOKEN = process.env.REACT_APP_BACKEND_TOKEN;

// ---- helpers
const statusColor = (st) => {
	switch (st || "OFFLINE") {
		case "IDLE":
			return "#f39c12";
		case "MOVING":
			return "#2980b9";
		case "CHARGING":
			return "#27ae60";
		default:
			return "#c0392b";
	}
};

const circleIcon = (color) => ({
	path: "M0,0a1,1 0 1,0 2,0a1,1 0 1,0 -2,0",
	scale: 6,
	fillColor: color,
	fillOpacity: 1,
	strokeColor: "#fff",
	strokeWeight: 2,
});

const dealerIcon = (active = false) => ({
	path: "M0,0a1,1 0 1,0 2,0a1,1 0 1,0 -2,0",
	scale: active ? 9 : 7,
	fillColor: active ? "#1976d2" : "#8e44ad",
	fillOpacity: 1,
	strokeColor: "#fff",
	strokeWeight: 2,
});

const norm = (s) => (s ?? "").toString().toLowerCase();

const vehicleMatches = (v, term) => {
	const t = norm(term);
	if (!t) return true;
	return [v.ch_no, v.dev_id, v.dlr_nm, v.ct, v.stt]
		.map(norm)
		.join(" ")
		.includes(t);
};

function scoreColor(scorePct) {
	if (scorePct >= 80) return "#57e32c"; // bright green
	if (scorePct >= 60) return "#b7dd29"; // yellow-green
	if (scorePct >= 40) return "#ffe234"; // yellow
	if (scorePct >= 20) return "#ffa534"; // orange
	return "#ff4545"; // red
}

const DEALER_OVERLAY_ARROW_HEIGHT = 18;

// ---------- loader wrapper
export default function Overview({
	maps,
	mode = "vehicles",
	filter,
	onFilterChange,
	vehiclesMap,
	counts,
	search,
	dealerId,
	dealers = [],
	selectedDealerId,
	onDealerMarkerSelect,
}) {
	if (maps?.loadError) return <div>Map failed to load</div>;
	if (!maps?.isLoaded) return null;
	return (
		<OverviewLoaded
			mode={mode}
			filter={filter}
			onFilterChange={onFilterChange}
			vehiclesMap={vehiclesMap}
			counts={counts}
			search={search}
			dealerId={dealerId}
			dealers={dealers}
			selectedDealerId={selectedDealerId}
			onDealerMarkerSelect={onDealerMarkerSelect}
		/>
	);
}

// ---------- main
function OverviewLoaded({
	mode = "vehicles",
	filter: controlledFilter,
	onFilterChange,
	vehiclesMap,
	counts,
	search,
	dealerId,
	dealers = [],
	selectedDealerId,
	onDealerMarkerSelect,
}) {
	const mapRef = useRef(null);
	const [selection, setSelection] = useState(null); // { type: "vehicle"|"dealer", id: string }
	const [uncontrolledFilter, setUncontrolledFilter] = useState("ALL");
	const [zoom, setZoom] = useState(6);

	const isDealerMode = mode === "dealers";
	const selectionType = selection?.type ?? null;
	const selectedId = selection?.id ?? null;

	const userInteractingRef = useRef(false);
	const allowAutoFitRef = useRef(true);
	const didInitialFitRef = useRef(false);

	const currentFilter = isDealerMode
		? "ALL"
		: controlledFilter ?? uncontrolledFilter;
	const setFilter = useCallback(
		(val) => {
			if (isDealerMode) return;
			if (typeof onFilterChange === "function") onFilterChange(val);
			else setUncontrolledFilter(val);
		},
		[onFilterChange, isDealerMode]
	);

	const byDealer = useCallback(
		(v) => {
			if (dealerId === "ALL" || dealerId === "" || dealerId == null)
				return true;
			const sel = String(dealerId);
			const vId =
				v.dlr_id != null
					? String(v.dlr_id)
					: v.dealer_id != null
					? String(v.dealer_id)
					: v.dealer?.id != null
					? String(v.dealer.id)
					: "";
			return vId === sel;
		},
		[dealerId]
	);

	const fitTo = useCallback(
		(points) => {
			allowAutoFitRef.current = true;
			if (!mapRef.current) return;
			if (!points?.length) {
				mapRef.current.panTo({ lat: 22.9734, lng: 78.6569 });
				mapRef.current.setZoom(6);
				setZoom(6);
				return;
			}
			// single marker — pan and set an appropriate zoom
			if (points.length === 1 && points[0]?.loc) {
				mapRef.current.panTo(points[0].loc);
				const targetZoom = isDealerMode ? 7 : 12;
				mapRef.current.setZoom(targetZoom);
				setZoom(targetZoom);
				return;
			}

			const bounds = new window.google.maps.LatLngBounds();
			points.forEach((p) => p?.loc && bounds.extend(p.loc));
			if (bounds.isEmpty()) {
				// fallback if loc missing after filter
				mapRef.current.panTo({ lat: 22.9734, lng: 78.6569 });
				mapRef.current.setZoom(6);
				setZoom(6);
				return;
			}

			mapRef.current.fitBounds(bounds, 48);
			let z = mapRef.current.getZoom();

			if (typeof z === "number") {
				if (isDealerMode) {
					const minZoom =
						points.length <= 3
							? points.length === 2
								? 6.5
								: 7
							: 6;
					const maxZoom = 8.5;
					let nextZoom = z;
					if (points.length <= 3 && z < minZoom) nextZoom = minZoom;
					if (nextZoom > maxZoom) nextZoom = maxZoom;
					if (nextZoom !== z) {
						mapRef.current.setZoom(nextZoom);
						z = nextZoom;
					}
				}
				setZoom(z);
			}
		},
		[isDealerMode]
	);

	// compute markers & center
	const { center, filteredMarkers } = useMemo(() => {
		const fallbackCenter = { lat: 22.9734, lng: 78.6569 };

		if (isDealerMode) {
			const list = (Array.isArray(dealers) ? dealers : [])
				.map((dealer) => {
					const lat =
						dealer?.loc?.lat != null
							? Number(dealer.loc.lat)
							: dealer?.latitude != null
							? Number(dealer.latitude)
							: null;
					const lng =
						dealer?.loc?.lng != null
							? Number(dealer.loc.lng)
							: dealer?.longitude != null
							? Number(dealer.longitude)
							: null;
					const valid =
						typeof lat === "number" &&
						!Number.isNaN(lat) &&
						typeof lng === "number" &&
						!Number.isNaN(lng) &&
						!(lat === 0 && lng === 0);
					if (!valid) return null;
					const id =
						dealer?.id ??
						dealer?._id ??
						dealer?.dealer_id ??
						dealer?.slug ??
						String(Math.random());
					return {
						id: String(id),
						loc: { lat, lng },
						name: dealer?.name ?? "Dealer",
						city: dealer?.city ?? "",
						state: dealer?.state ?? "",
						vehicle_count: dealer?.vehicle_count ?? 0,
						raw: dealer,
					};
				})
				.filter(Boolean);

			if (list.length === 0)
				return { center: fallbackCenter, filteredMarkers: [] };

			if (list.length === 1)
				return { center: { ...list[0].loc }, filteredMarkers: list };

			const { minLat, maxLat, minLng, maxLng } = list.reduce(
				(acc, v) => ({
					minLat: Math.min(acc.minLat, v.loc.lat),
					maxLat: Math.max(acc.maxLat, v.loc.lat),
					minLng: Math.min(acc.minLng, v.loc.lng),
					maxLng: Math.max(acc.maxLng, v.loc.lng),
				}),
				{
					minLat: Infinity,
					maxLat: -Infinity,
					minLng: Infinity,
					maxLng: -Infinity,
				}
			);

			return {
				center: {
					lat: (minLat + maxLat) / 2,
					lng: (minLng + maxLng) / 2,
				},
				filteredMarkers: list,
			};
		}

		const list = Object.entries(vehiclesMap || {}).map(([id, v]) => ({
			id: Number(id),
			...v,
		}));

		const pts = list
			.filter(byDealer)
			.map((v) => ({
				...v,
				loc:
					v?.loc &&
					v.loc.lat != null &&
					v.loc.lng != null &&
					!(v.loc.lat === 0 && v.loc.lng === 0)
						? { lat: Number(v.loc.lat), lng: Number(v.loc.lng) }
						: null,
			}))
			.filter((v) => v.loc)
			.filter((v) => vehicleMatches(v, search));

		const filtered =
			currentFilter === "ALL"
				? pts
				: pts.filter((v) => v.st === currentFilter);

		const base = filtered.length ? filtered : pts;
		if (base.length === 0)
			return { center: fallbackCenter, filteredMarkers: filtered };

		if (base.length === 1)
			return {
				center: { ...base[0].loc },
				filteredMarkers: filtered,
			};

		const { minLat, maxLat, minLng, maxLng } = base.reduce(
			(acc, v) => ({
				minLat: Math.min(acc.minLat, v.loc.lat),
				maxLat: Math.max(acc.maxLat, v.loc.lat),
				minLng: Math.min(acc.minLng, v.loc.lng),
				maxLng: Math.max(acc.maxLng, v.loc.lng),
			}),
			{
				minLat: Infinity,
				maxLat: -Infinity,
				minLng: Infinity,
				maxLng: -Infinity,
			}
		);

		return {
			center: {
				lat: (minLat + maxLat) / 2,
				lng: (minLng + maxLng) / 2,
			},
			filteredMarkers: filtered,
		};
	}, [isDealerMode, dealers, vehiclesMap, byDealer, search, currentFilter]);

	const legendCounts = useMemo(() => {
		if (isDealerMode) return {};
		const acc = { ALL: 0, IDLE: 0, MOVING: 0, CHARGING: 0, OFFLINE: 0 };
		const all = Object.values(vehiclesMap || {}).filter(byDealer);
		const t = norm(search);

		const source =
			t.length === 0 ? all : all.filter((v) => vehicleMatches(v, t));
		for (const v of source) {
			acc.ALL += 1;
			if (acc[v.st] != null) acc[v.st] += 1;
		}

		// if no search and no dealer filter, fall back to backend counts for consistency
		const noDealer =
			dealerId === "ALL" || dealerId === "" || dealerId == null;
		return t.length === 0 && noDealer ? counts : acc;
	}, [isDealerMode, vehiclesMap, byDealer, search, counts, dealerId]);

	useEffect(() => {
		if (!isDealerMode) {
			if (selectionType === "dealer") setSelection(null);
			didInitialFitRef.current = false;
			if (filteredMarkers.length) fitTo(filteredMarkers);
			return;
		}
		if (selectionType === "vehicle") setSelection(null);
		didInitialFitRef.current = false;
	}, [isDealerMode, selectionType, filteredMarkers, fitTo]);

	useEffect(() => {
		if (!isDealerMode) return;
		if (selectedDealerId == null) {
			if (selectionType === "dealer") setSelection(null);
			return;
		}
		const nextId = String(selectedDealerId);
		if (selectionType !== "dealer" || selectedId !== nextId) {
			setSelection({ type: "dealer", id: nextId });
		}
	}, [isDealerMode, selectedDealerId, selectionType, selectedId]);

	useEffect(() => {
		if (filteredMarkers.length === 0) return;

		if (!didInitialFitRef.current) {
			didInitialFitRef.current = true;
			fitTo(filteredMarkers);
			return;
		}

		// re-fit when filter, dealer, or search changes and user is not manipulating
		if (!userInteractingRef.current) {
			if (isDealerMode && selectedDealerId != null) return;
			fitTo(filteredMarkers);
		}
	}, [
		filteredMarkers,
		currentFilter,
		dealerId,
		search,
		isDealerMode,
		selectedDealerId,
		fitTo,
	]);

	useEffect(() => {
		if (!isDealerMode) return;
		if (!filteredMarkers.length) return;
		if (!mapRef.current) return;
		userInteractingRef.current = false;
		fitTo(filteredMarkers);
	}, [isDealerMode, filteredMarkers, fitTo]);

	useEffect(() => {
		if (!isDealerMode) return;
		if (selectedDealerId == null) return;
		if (!mapRef.current) return;
		const match = filteredMarkers.find(
			(m) => String(m.id) === String(selectedDealerId)
		);
		if (!match?.loc) return;
		userInteractingRef.current = false;
		mapRef.current.panTo(match.loc);
		const nextZoom = mapRef.current.getZoom();
		if (typeof nextZoom === "number" && nextZoom < 8) {
			mapRef.current.setZoom(8);
			setZoom(8);
		}
	}, [isDealerMode, selectedDealerId, filteredMarkers]);

	// Recenter handler
	const handleRecenter = useCallback(() => {
		userInteractingRef.current = false;
		fitTo(filteredMarkers);
	}, [filteredMarkers, fitTo]);

	const handleMarkerClick = useCallback(
		(marker) => {
			if (!marker) return;
			if (isDealerMode) {
				const id = String(marker.id);
				setSelection({ type: "dealer", id });
				if (typeof onDealerMarkerSelect === "function") {
					onDealerMarkerSelect(id);
				}
			} else {
				const id = String(marker.id);
				setSelection({ type: "vehicle", id });
			}
		},
		[isDealerMode, onDealerMarkerSelect]
	);

	const handleInfoWindowClose = useCallback(() => {
		if (isDealerMode) {
			setSelection(null);
			if (typeof onDealerMarkerSelect === "function") {
				onDealerMarkerSelect(null);
			}
		} else {
			setSelection(null);
		}
	}, [isDealerMode, onDealerMarkerSelect]);

	const [dealerOverlaySize, setDealerOverlaySize] = useState({
		width: 0,
		height: 0,
	});

	const activeDealer =
		isDealerMode && selectionType === "dealer"
			? filteredMarkers.find((marker) => String(marker.id) === selectedId)
			: null;
	const activeVehicle =
		selectionType === "vehicle" && selectedId != null
			? vehiclesMap[selectedId] || null
			: null;

	const isDealerSelection =
		isDealerMode && selectionType === "dealer" && !!activeDealer;

	const infoPosition = useMemo(() => {
		if (isDealerSelection) {
			return activeDealer?.loc ?? null;
		}
		if (selectionType === "vehicle" && activeVehicle) {
			if (activeVehicle.loc) {
				return {
					lat: activeVehicle.loc.lat,
					lng: activeVehicle.loc.lng,
				};
			}
			return center;
		}
		return null;
	}, [isDealerSelection, activeDealer, selectionType, activeVehicle, center]);

	const infoContent = useMemo(() => {
		if (selectionType === "vehicle" && activeVehicle) {
			return <Details id={selectedId} v={activeVehicle} />;
		}
		return null;
	}, [selectionType, activeVehicle, selectedId]);

	const showDealerOverlay = Boolean(isDealerSelection && infoPosition && activeDealer);

	return (
		<GoogleMap
			mapContainerStyle={{ width: "100%", height: "97vh" }}
			defaultZoom={6}
			defaultCenter={{ lat: 22.9734, lng: 78.6569 }}
			options={{
				streetViewControl: false,
				mapTypeControl: false,
				fullscreenControl: false,
				rotateControl: false,
				scaleControl: false,
				zoomControl: true,
				gestureHandling: "greedy",
				clickableIcons: false,
			}}
			onLoad={(m) => {
				mapRef.current = m;
				setZoom(m.getZoom());
				if (filteredMarkers.length > 0 && !didInitialFitRef.current) {
					didInitialFitRef.current = true;
					fitTo(filteredMarkers);
				}
			}}
			onDragStart={() => {
				userInteractingRef.current = true;
			}}
			onZoomChanged={() => {
				const z = mapRef.current?.getZoom();
				if (typeof z === "number") setZoom(z);
				userInteractingRef.current = true;
			}}
			onIdle={() => {
				// user finished interacting → allow future auto-fit
				userInteractingRef.current = true;
			}}
		>
			{/* markers */}
			{isDealerMode ? (
				filteredMarkers.map((marker) => (
					<Marker
						key={marker.id}
						position={marker.loc}
						icon={dealerIcon(
							selectionType === "dealer" &&
								String(marker.id) === selectedId
						)}
						onClick={() => handleMarkerClick(marker)}
					/>
				))
			) : zoom < 16 ? (
				<MarkerClustererF
					key={`${currentFilter}:${filteredMarkers.length}:${norm(
						search
					)}:${dealerId}`}
					options={{
						gridSize: 60,
						minimumClusterSize: 2,
						imagePath:
							"https://raw.githubusercontent.com/googlemaps/v3-utility-library/master/markerclustererplus/images/m",
					}}
				>
					{(clusterer) =>
						filteredMarkers.map((v) => (
							<Marker
								key={v.id}
								clusterer={clusterer}
								position={v.loc}
								icon={circleIcon(statusColor(v.st))}
								onClick={() => handleMarkerClick(v)}
							/>
						))
					}
				</MarkerClustererF>
			) : (
				filteredMarkers.map((v) => (
					<Marker
						key={v.id}
						position={v.loc}
						icon={circleIcon(statusColor(v.st))}
						onClick={() => handleMarkerClick(v)}
					/>
				))
			)}

			{/* info window */}
			{showDealerOverlay ? (
				<OverlayView
					position={infoPosition}
					mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
					getPixelPositionOffset={(width = 0) => {
						const overlayWidth =
							dealerOverlaySize.width || width || 280;
						const cardHeight = dealerOverlaySize.height || 220;
						const totalHeight =
							cardHeight + DEALER_OVERLAY_ARROW_HEIGHT;
						return {
							x: -Math.round(overlayWidth / 2),
							y: -Math.round(totalHeight),
						};
					}}
					>
						<DealerOverlay
							dealer={activeDealer.raw || activeDealer}
							onClose={handleInfoWindowClose}
							onMeasure={setDealerOverlaySize}
						/>
				</OverlayView>
			) : (
				!isDealerSelection &&
				infoContent &&
				infoPosition && (
					<InfoWindow
						position={infoPosition}
						onCloseClick={handleInfoWindowClose}
					>
						{infoContent}
					</InfoWindow>
				)
			)}

			{/* recenter button */}
			<div
				style={{
					position: "absolute",
					top: 16,
					right: 16,
					zIndex: 10,
				}}
			>
				<button
					onClick={handleRecenter}
					style={{
						background: "#fff",
						border: "1px solid #ccc",
						borderRadius: 6,
						padding: "6px 10px",
						cursor: "pointer",
						fontSize: 13,
						boxShadow: "0 1px 4px rgba(0,0,0,0.3)",
					}}
				>
					⟳ Recenter
				</button>
			</div>

			{/* status legend */}
			{!isDealerMode && (
				<div
					style={{
						position: "absolute",
						top: 16,
						left: 16,
						zIndex: 10,
					}}
				>
					<div
						style={{
							background: "#fff",
							border: "1px solid #ccc",
							borderRadius: 8,
							padding: "8px 10px",
							fontSize: 12,
							boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
							minWidth: 160,
						}}
					>
						{[
							// legend items
							{
								key: "ALL",
								label: "ALL",
								color: "#000",
								icon: (
									<ElectricRickshaw
										sx={{ fontSize: 14, color: "#000" }}
									/>
								),
								count: legendCounts.ALL,
							},
							{
								key: "IDLE",
								label: "IDLE",
								color: statusColor("IDLE"),
								count: legendCounts.IDLE,
							},
							{
								key: "MOVING",
								label: "MOVING",
								color: statusColor("MOVING"),
								count: legendCounts.MOVING,
							},
							{
								key: "CHARGING",
								label: "CHARGING",
								color: statusColor("CHARGING"),
								count: legendCounts.CHARGING,
							},
							{
								key: "OFFLINE",
								label: "OFFLINE",
								color: statusColor("OFFLINE"),
								count: legendCounts.OFFLINE,
							},
						].map((item) => {
							const active = currentFilter === item.key;
							return (
								<button
									key={item.key}
									onClick={() => setFilter(item.key)}
									style={{
										width: "100%",
										display: "flex",
										alignItems: "center",
										justifyContent: "space-between",
										gap: 8,
										marginBottom: 6,
										padding: "6px 8px",
										borderRadius: 6,
										border:
											"1px solid " +
											(active ? item.color : "#ddd"),
										background: active
											? "rgba(0,0,0,0.04)"
											: "#fff",
										cursor: "pointer",
									}}
								>
									<div
										style={{
											display: "flex",
											alignItems: "center",
											gap: 6,
										}}
									>
										{item.icon ? (
											item.icon
										) : (
											<span
												style={{
													display: "inline-block",
													width: 10,
													height: 10,
													borderRadius: "50%",
													background: item.color,
													border: "2px solid #fff",
													boxShadow:
														"0 0 0 1px #ccc inset",
												}}
											/>
										)}
										<span
											style={{
												color: "#444",
												fontWeight: active ? 700 : 500,
											}}
										>
											{item.label}
										</span>
									</div>
									<strong
										style={{
											color: active ? item.color : "#333",
										}}
									>
										{item.count}
									</strong>
								</button>
							);
						})}
					</div>
				</div>
			)}
		</GoogleMap>
	);
}

function DealerDetails({ dealer }) {
	const dealerKey = useMemo(() => {
		if (!dealer) return null;
		const candidates = [
			dealer.id,
			dealer._id,
			dealer.dealer_id,
			dealer.slug,
			dealer.code,
		];
		for (const value of candidates) {
			if (value != null && value !== "") return String(value);
		}
		return null;
	}, [dealer]);

	const [score, setScore] = useState(null);
	const [loadingScore, setLoadingScore] = useState(false);
	const [scoreError, setScoreError] = useState(false);

	useEffect(() => {
		if (!dealerKey) {
			setScore(null);
			setLoadingScore(false);
			setScoreError(false);
			return;
		}

		const ac = new AbortController();
		setLoadingScore(true);
		setScoreError(false);

		fetchDealerScore(dealerKey, ac.signal)
			.then((data) => {
				setScore(data);
				setScoreError(false);
			})
			.catch((error) => {
				if (error?.name === "AbortError") return;
				setScore(null);
				setScoreError(true);
			})
			.finally(() => {
				setLoadingScore(false);
			});

		return () => ac.abort();
	}, [dealerKey]);

	const name = dealer?.name ?? "Dealer";
	const city = dealer?.city ?? "";
	const state = dealer?.state ?? "";
	const vehicles =
		dealer?.vehicle_count ?? dealer?.vehicleCount ?? dealer?.count ?? 0;

	const breakdown = score?.breakdown ?? {};
	const pctToStars = (value) =>
		typeof value === "number" ? Math.round((value / 20) * 10) / 10 : null;
	const scorePct =
		typeof breakdown.composite_vehicle_score === "number"
			? breakdown.composite_vehicle_score
			: null;
	const stars = pctToStars(scorePct);
	const segments = [
		{ key: "Performance", value: breakdown.performance_score },
		{ key: "Health", value: breakdown.health_score },
		{ key: "Energy", value: breakdown.energy_score },
	];
	return (
		<Box sx={{ minWidth: 220, maxWidth: 280 }}>
			<Typography variant="subtitle1" fontWeight={600}>
				{name}
			</Typography>
			{(city || state) && (
				<Typography
					variant="body2"
					color="text.secondary"
					sx={{ mt: 0.5 }}
				>
					{[city, state].filter(Boolean).join(", ")}
				</Typography>
			)}

			<Box
				sx={{
					mt: 1.25,
					pt: 1,
					borderTop: "1px solid rgba(0,0,0,0.08)",
				}}
			>
				<Typography variant="subtitle2" fontWeight={600}>
					Dealer score
				</Typography>
				{loadingScore ? (
					<Typography
						variant="caption"
						color="text.secondary"
						sx={{ display: "block", mt: 0.75 }}
					>
						Loading score…
					</Typography>
				) : scoreError ? (
					<Typography
						variant="caption"
						color="error"
						sx={{ display: "block", mt: 0.75 }}
					>
						Unable to load dealer score right now.
					</Typography>
				) : stars == null ? (
					<Typography
						variant="caption"
						color="text.secondary"
						sx={{ display: "block", mt: 0.75 }}
					>
						Score unavailable.
					</Typography>
				) : (
					<>
						<Box
							display="flex"
							alignItems="center"
							justifyContent="center"
							mt={0.75}
							gap={0.75}
						>
							<Tooltip
								arrow
								placement="top"
								title={
									<Box
										sx={{
											display: "grid",
											gridTemplateColumns: "auto auto",
											alignItems: "center",
											columnGap: 1,
											rowGap: 0.25,
										}}
									>
										{segments.map(({ key, value }) => {
											const segmentStars = pctToStars(
												typeof value === "number"
													? value
													: null
											);
											return (
												<React.Fragment key={key}>
													<Typography
														variant="caption"
														sx={{ color: "#999" }}
													>
														{key}
													</Typography>
													<Box
														component="span"
														sx={{
															display:
																"inline-flex",
															alignItems:
																"center",
															gap: 0.5,
														}}
													>
														<Rating
															value={segmentStars}
															max={5}
															precision={0.5}
															size="small"
															readOnly
															sx={{
																"& .MuiRating-iconFilled":
																	{
																		color: scoreColor(
																			typeof value ===
																				"number"
																				? value
																				: 0
																		),
																	},
																"& .MuiRating-iconEmpty":
																	{
																		color: "#ddd",
																	},
															}}
														/>
														<Typography variant="caption">
															{segmentStars !=
															null
																? `${segmentStars.toFixed(
																		1
																  )}/5`
																: "—"}
														</Typography>
													</Box>
												</React.Fragment>
											);
										})}
									</Box>
								}
							>
								<Box
									component="span"
									sx={{ display: "inline-flex" }}
								>
									<Rating
										value={stars}
										max={5}
										precision={0.5}
										size="small"
										readOnly
										sx={{
											"& .MuiRating-iconFilled": {
												color: scoreColor(scorePct),
											},
											"& .MuiRating-iconEmpty": {
												color: "#ddd",
											},
										}}
									/>
								</Box>
							</Tooltip>
							{scorePct != null && (
								<Typography variant="body2" fontWeight={600}>
									{scorePct.toFixed(1)}%
								</Typography>
							)}
						</Box>
					</>
				)}
			</Box>

			<Typography variant="body2" sx={{ mt: 1.25 }}>
				Vehicles: <strong>{vehicles}</strong>
			</Typography>
			{dealerKey && (
				<Box sx={{ mt: 1.5, textAlign: "center" }}>
					<Button
						variant="contained"
						size="small"
						sx={{
							textTransform: "none",
							fontSize: 11,
							px: 2.5,
							py: 0.5,
							borderRadius: 1.5,
						}}
						onClick={() =>
							window.open(`/dealers/${dealerKey}`, "_blank")
						}
					>
						View dealer
					</Button>
				</Box>
			)}
		</Box>
	);
}

function DealerOverlay({ dealer, onClose, onMeasure }) {
	const paperRef = useCallback(
		(node) => {
			if (!node || typeof onMeasure !== "function") return;
			const rect = node.getBoundingClientRect();
			onMeasure((prev) => {
				const next = {
					width: Math.round(rect.width),
					height: Math.round(rect.height),
				};
				if (
					prev &&
					prev.width === next.width &&
					prev.height === next.height
				) {
					return prev;
				}
				return next;
			});
		},
		[onMeasure]
	);

	if (!dealer) return null;

	return (
		<Box
			sx={{
				position: "relative",
				pointerEvents: "auto",
			}}
		>
			<Paper
				ref={paperRef}
				sx={{
					position: "relative",
					p: 2,
					pt: 3.5,
					borderRadius: 3,
					boxShadow: 6,
					minWidth: 260,
					maxWidth: 320,
				}}
			>
				<IconButton
					onClick={onClose}
					size="small"
					sx={{
						position: "absolute",
						top: 8,
						right: 8,
						color: "text.secondary",
						backgroundColor: "rgba(255,255,255,0.8)",
						boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
						"&:hover": {
							backgroundColor: "rgba(245,245,245,0.9)",
						},
					}}
				>
					<CloseRounded fontSize="small" />
				</IconButton>
				<DealerDetails dealer={dealer} />
			</Paper>
		</Box>
	);
}
function Details({ id, v }) {
	// states
	const [score, setScore] = useState(null);

	const b = score?.breakdown;
	const pctToStars = (p) =>
		p == null ? null : Math.round((p / 20) * 10) / 10;
	const segs = [
		{ k: "Performance", pct: b?.performance_score },
		{ k: "Health", pct: b?.health_score },
		{ k: "Energy", pct: b?.energy_score },
	];

	// effects
	useEffect(() => {
		const ac = new AbortController();
		fetchVehicleScore(id, ac.signal)
			.then(setScore)
			.catch(() => setScore(null));
		return () => ac.abort();
	}, [id]);

	// stars
	const scorePct = score?.breakdown?.composite_vehicle_score ?? null; // 0–100
	const stars =
		scorePct == null ? null : Math.round((scorePct / 20) * 10) / 10;

	// fetch helper
	async function fetchVehicleScore(vehId, signal) {
		const url = `${BACKEND_URL}vehicles/${vehId}/score/`;
		const res = await fetch(url, {
			method: "GET",
			headers: {
				"Content-Type": "application/json",
				...(BACKEND_TOKEN
					? { Authorization: `Bearer ${BACKEND_TOKEN}` }
					: {}),
			},
			signal,
		});
		if (!res.ok) throw new Error(`HTTP ${res.status}`);
		const j = await res.json();
		const d = j?.data ?? {};
		const s = d.scores_rolling_avg ?? {};
		const pct =
			typeof s.composite_vehicle_score === "number"
				? s.composite_vehicle_score
				: typeof d.composite_vehicle_score === "number"
				? d.composite_vehicle_score
				: null;

		return {
			rolling_basis: d.rolling_basis ?? null,
			weeks_considered: d.weeks_considered ?? [],
			sample_count: d.sample_count ?? null,
			breakdown: {
				performance_score:
					typeof s.performance_score === "number"
						? s.performance_score
						: null,
				health_score:
					typeof s.health_score === "number" ? s.health_score : null,
				energy_score:
					typeof s.energy_score === "number" ? s.energy_score : null,
				composite_vehicle_score: pct,
			},
		};
	}

	const st = v.st || "OFFLINE";
	const al = Number(v.al ?? 0);

	const statusMap = {
		IDLE: {
			color: "#f39c12",
			icon: <HourglassFull sx={{ fontSize: 16 }} />,
			label: "IDLE",
		},
		MOVING: {
			color: "#2980b9",
			icon: <Moving sx={{ fontSize: 16 }} />,
			label: "MOVING",
		},
		CHARGING: {
			color: "#27ae60",
			icon: <EvStation sx={{ fontSize: 16 }} />,
			label: "CHARGING",
		},
		OFFLINE: {
			color: "#c0392b",
			icon: <ElectricRickshaw sx={{ fontSize: 16 }} />,
			label: "OFFLINE",
		},
	};
	const vehStatus = statusMap[st] || statusMap.OFFLINE;

	return (
		<Box sx={{ minWidth: 240, pt: 0.25, px: 0.5, pb: 0.75 }}>
			{/* --- Chassis number --- */}
			<Typography
				variant="subtitle2"
				sx={{
					textAlign: "center",
					fontWeight: 700,
					fontSize: 11,
					lineHeight: "14px",
					mb: 0.25, // was 1
					color: "#333",
					wordBreak: "break-all",
				}}
			>
				{v.ch_no}
			</Typography>

			<Grid
				container
				spacing={0.5} // was 1
				justifyContent="center"
				alignItems="center"
				sx={{ mb: 0.5 }} // was 1
			>
				<Grid item xs={6}>
					<Box
						display="flex"
						alignItems="center"
						justifyContent="center"
						gap={0.4}
						sx={{ color: vehStatus.color }}
					>
						{React.cloneElement(vehStatus.icon, {
							sx: { fontSize: 14 },
						})}{" "}
						{/* was 16 */}
						<Typography variant="caption" fontWeight={600}>
							{vehStatus.label}
						</Typography>
					</Box>
				</Grid>
				<Grid item xs={6}>
					<Box
						display="flex"
						alignItems="center"
						justifyContent="center"
					>
						{al > 0 ? (
							<Badge
								badgeContent={al}
								color="error"
								sx={{
									"& .MuiBadge-badge": {
										fontSize: 8,
										height: 13,
										minWidth: 13,
									},
								}}
							>
								<WarningAmber
									sx={{ fontSize: 14, color: "#c0392b" }}
								/>
							</Badge>
						) : (
							<Box
								display="flex"
								alignItems="center"
								gap={0.3}
								sx={{ color: "#27ae60" }}
							>
								<WarningAmber sx={{ fontSize: 14 }} />
								<Typography variant="caption" fontWeight={600}>
									OK
								</Typography>
							</Box>
						)}
					</Box>
				</Grid>
			</Grid>

			{/* --- Info grid --- */}
			<Grid container spacing={0.5} sx={{ mb: 1 }}>
				<Grid item xs={12}>
					<Box display="flex" justifyContent="space-between">
						<Typography variant="caption" color="#555">
							Dealer
						</Typography>
						<Typography variant="caption" fontWeight={600}>
							{v.dlr_nm}
						</Typography>
					</Box>
				</Grid>
				<Grid item xs={12}>
					<Box display="flex" justifyContent="space-between">
						<Typography variant="caption" color="#555">
							City
						</Typography>
						<Typography variant="caption" fontWeight={600}>
							{v.ct}, {v.stt}
						</Typography>
					</Box>
				</Grid>
				{v.loc &&
					Number.isFinite(v.loc.lat) &&
					Number.isFinite(v.loc.lng) && (
						<Grid item xs={12}>
							<Box display="flex" justifyContent="space-between">
								<Typography variant="caption" color="#555">
									Location
								</Typography>
								<Typography variant="caption" fontWeight={600}>
									{v.loc.lat.toFixed(6)},
									{v.loc.lng.toFixed(6)}
								</Typography>
							</Box>
						</Grid>
					)}
			</Grid>

			{/* --- Score --- */}
			<Box
				display="flex"
				alignItems="center"
				justifyContent="center"
				flexDirection="column"
				mb={1}
			>
				{stars == null ? (
					<Typography variant="caption" fontWeight={600}>
						—
					</Typography>
				) : (
					<Tooltip
						arrow
						placement="top"
						title={
							<Box
								sx={{
									display: "grid",
									gridTemplateColumns: "auto auto",
									alignItems: "center",
									columnGap: 1,
									rowGap: 0.25,
								}}
							>
								{segs.map(({ k, pct }) => {
									const s = pctToStars(pct);
									return (
										<React.Fragment key={k}>
											<Typography
												variant="caption"
												sx={{ color: "#999" }}
											>
												{k}
											</Typography>
											<Box
												component="span"
												sx={{
													display: "inline-flex",
													alignItems: "center",
													gap: 0.5,
												}}
											>
												<Rating
													value={s}
													max={5}
													precision={0.5}
													size="small"
													readOnly
													sx={{
														"& .MuiRating-iconFilled":
															{
																color: scoreColor(
																	pct ?? 0
																),
															},
														"& .MuiRating-iconEmpty":
															{ color: "#ddd" },
													}}
												/>
												<Typography variant="caption">
													{s?.toFixed(1) ?? "—"}/5
												</Typography>
											</Box>
										</React.Fragment>
									);
								})}
							</Box>
						}
					>
						<Box component="span" sx={{ display: "inline-flex" }}>
							<Rating
								value={stars}
								max={5}
								precision={0.5}
								size="small"
								readOnly
								sx={{
									"& .MuiRating-iconFilled": {
										color: scoreColor(scorePct),
									},
									"& .MuiRating-iconEmpty": { color: "#ddd" },
								}}
							/>
						</Box>
					</Tooltip>
				)}
			</Box>

			<Box textAlign="center" mt={1}>
				<Button
					size="small"
					variant="contained"
					sx={{
						textTransform: "none",
						fontSize: 11,
						px: 2,
						py: 0.5,
						borderRadius: 1.5,
						backgroundColor: "#2980b9",
						"&:hover": { backgroundColor: "#2471a3" },
					}}
					onClick={() => window.open(`/vehicles/${id}`, "_blank")}
				>
					Go to vehicle
				</Button>
			</Box>
		</Box>
	);
}
